import { useState } from 'react';
import {
  keepPreviousData, useMutation, useQuery, useQueryClient, useSuspenseQuery,
} from '@tanstack/react-query';

import { Callout, Field, buttonClass, inputClass } from '@/components/ui';
import {
  adminJobsQuery, adminLocationsQuery, adminStockQuery, adminWasteQuery,
} from '@/queries';

/**
 * Waste. Spec 003 US-3, task T078, FR-208.
 *
 * The one screen where the cost boundary is visible in the interface rather
 * than only in the database. A staff account sees every misprint, what it was,
 * how much of it there was and who was on the machine. It does not see what any
 * of it cost.
 *
 * That is one query for both audiences: `job_waste` nulls the cost columns for
 * a caller without the costs right, so there is no second code path here to
 * drift out of step with the rule (R-90, R-92).
 *
 * The point of the report is FR-208: waste grouped by job, by machine or by
 * operator. Not to catch anybody out, but because a misprint rate that clusters
 * on one machine is a machine that needs servicing.
 *
 * **The grouping is the database's.** This used to fetch 500 raw spoils and
 * group them here, which is correct arithmetic over the wrong set the moment
 * there are more than 500: every total quietly shrank to the window. Paging the
 * rows would have made it worse, because fifty rows grouped into "Heat press,
 * 12 units" reads exactly like the truth. A report over a range is an
 * aggregate, so the aggregate is what is asked for. R-231.
 */

type GroupBy = 'reason' | 'item' | 'operator' | 'machine';

const GROUP_LABEL: Record<GroupBy, string> = {
  reason: 'Reason',
  item: 'Item',
  operator: 'Operator',
  machine: 'Machine',
};


/**
 * Recording a spoiled unit. R-273.
 *
 * The report could be read and not written, so it would have stayed empty for
 * ever, and an empty waste report reads as "we waste nothing" rather than as
 * "nobody has told us".
 *
 * The job is required because `job_consumption.job_id` is, and that is the
 * design: three misprinted shirts belong to the run that misprinted them.
 * Without it the report says how much was spoiled and never which work spoiled
 * it, which is the only version of the question worth asking.
 */
function RecordWaste() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [draft, setDraft] = useState({
    job_id: '', item_id: '', location_id: '', qty: '', reason: 'misprint',
  });

  const { jobs } = useSuspenseQuery(adminJobsQuery).data;
  /**
   * ANY item, not just the consumables. R-273.
   *
   * The first version offered `materials`, which is ink, film and thread. The
   * main waste in garment decoration is a misprinted SHIRT, and a shirt is a
   * garment, so the one thing people most need to record was the one thing the
   * list left out.
   *
   * A select cannot hold 3,338 items, so this searches. Two characters is
   * enough to narrow it and short enough not to feel like a form.
   */
  const [itemSearch, setItemSearch] = useState('');
  const { data: itemHits } = useQuery({
    ...adminStockQuery({ page: 1, q: itemSearch, kind: 'all', low: false }),
    enabled: itemSearch.trim().length >= 2,
  });

  const items = ((itemHits?.rows ?? []) as Array<{
    item_id: string; items?: { code?: string; name?: string } | null;
  }>).reduce<Array<{ id: string; code: string; name: string }>>((list, r) => {
    // One row per item: stock is per item AND location, so the same item
    // appears once for every bay it sits in.
    if (list.some((x) => x.id === r.item_id)) return list;
    list.push({ id: r.item_id, code: r.items?.code ?? '', name: r.items?.name ?? '' });
    return list;
  }, []);
  const { locations } = useSuspenseQuery(adminLocationsQuery).data as unknown as {
    locations: Array<{ id: string; code: string; label: string }>;
  };

  // Work in progress only. Waste belongs to a run somebody is doing, and a list
  // of every job ever finished is a list nobody can find today's job in.
  const openJobs = jobs.filter((j) => j.stage !== 'complete');

  const record = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/waste', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(draft),
      });
      const body = (await res.json().catch(() => null)) as
        | { message?: string; error?: { message?: string } } | null;
      if (!res.ok) throw new Error(body?.error?.message ?? 'Could not record it.');
      return body?.message ?? 'Recorded.';
    },
    onSuccess: (message) => {
      setSaid(message);
      setDraft((d) => ({ ...d, qty: '' }));
      void qc.invalidateQueries({ queryKey: ['admin', 'waste'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'stock'] });
    },
    onError: (e: Error) => setSaid(e.message),
  });

  if (!open) {
    return (
      <button type="button" className={buttonClass('primary', 'sm')} onClick={() => setOpen(true)}>
        Record waste
      </button>
    );
  }

  if (openJobs.length === 0) {
    return (
      <Callout tone="warn" title="There is no job to record waste against">
        Waste belongs to the run that produced it, so there has to be a job on the
        board first. Open the job board and start one, then come back.
      </Callout>
    );
  }

  return (
    <form
      className="w-full rounded-[10px] border border-line bg-panel p-4"
      onSubmit={(e) => { e.preventDefault(); setSaid(null); record.mutate(); }}
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Field label="Job" htmlFor="w-job" hint="The run it was spoiled on">
          <select
            id="w-job" className={inputClass} required value={draft.job_id}
            onChange={(e) => setDraft((d) => ({ ...d, job_id: e.target.value }))}
          >
            <option value="">Choose a job</option>
            {openJobs.map((j) => (
              <option key={j.id} value={j.id}>{j.ref}</option>
            ))}
          </select>
        </Field>
        <Field label="Item" htmlFor="w-item" hint="Search a code or a name">
          <input
            id="w-item"
            className={inputClass}
            placeholder="Type two letters"
            value={itemSearch}
            onChange={(e) => { setItemSearch(e.target.value); setDraft((d) => ({ ...d, item_id: '' })); }}
          />
          {items.length > 0 && (
            <select
              className={`${inputClass} mt-1`}
              required
              value={draft.item_id}
              onChange={(e) => setDraft((d) => ({ ...d, item_id: e.target.value }))}
            >
              <option value="">Choose from {items.length}</option>
              {items.map((m) => (
                <option key={m.id} value={m.id}>{m.code} {m.name}</option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Location" htmlFor="w-loc" hint="Where it came off">
          <select
            id="w-loc" className={inputClass} required value={draft.location_id}
            onChange={(e) => setDraft((d) => ({ ...d, location_id: e.target.value }))}
          >
            <option value="">Choose a location</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>{l.code} {l.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Quantity" htmlFor="w-qty" hint="In the item's own unit">
          <input
            id="w-qty" className={inputClass} required inputMode="decimal" value={draft.qty}
            onChange={(e) => setDraft((d) => ({ ...d, qty: e.target.value }))}
          />
        </Field>
        <Field label="What went wrong" htmlFor="w-reason" hint="The report groups by this">
          <select
            id="w-reason" className={inputClass} required value={draft.reason}
            onChange={(e) => setDraft((d) => ({ ...d, reason: e.target.value }))}
          >
            {['misprint', 'misaligned', 'ink bleed', 'wrong colour', 'wrong size',
              'machine fault', 'damaged blank', 'operator error', 'other'].map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </Field>
      </div>

      {said && (
        <Callout
          tone={said.includes('recorded') ? 'ok' : 'danger'}
          title={said}
          className="mt-3"
        />
      )}

      <div className="mt-3 flex items-center gap-2">
        <button type="submit" className={buttonClass('primary', 'sm')} disabled={record.isPending}>
          {record.isPending ? 'Recording' : 'Record it'}
        </button>
        <button
          type="button" className={buttonClass('secondary', 'sm')}
          onClick={() => { setOpen(false); setSaid(null); }}
        >
          Cancel
        </button>
      </div>

      {/* Stock leaves the shelf when this is recorded, in the same transaction.
          Saying so stops somebody recording it twice "to be sure". */}
      <p className="mt-3 text-[12.5px] text-muted">
        This takes the quantity off the shelf as well as recording it here. The two
        cannot disagree: they are written together.
      </p>
    </form>
  );
}

/**
 * A count, or zero. Never NaN. R-284.
 *
 * `Number(undefined)` is NaN, and React prints NaN into the page, so one row
 * that arrives without a count writes "NaN" where a number belongs. It read
 * NaN under the render harness and nobody saw it, because a warning on stderr
 * is not a failing test. A missing count is zero on this screen: the row
 * exists, so something was wasted, and no figure is worse than no row.
 */
function num(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export default function AdminWastePage() {
  const [by, setBy] = useState<GroupBy>('reason');
  const { data } = useQuery({
    ...adminWasteQuery(by),
    placeholderData: keepPreviousData,
  });

  const groups = data?.rows ?? [];
  const totals = data?.totals ?? { entries: 0, qty: 0, cost_cents: null };

  // If the total carries a cost, this caller may see costs. Reading it from the
  // data rather than from a role means the screen cannot disagree with the
  // database about who is allowed what.
  const showsCost = totals.cost_cents != null;
  const rows = groups;
  const totalCost = totals.cost_cents ?? 0;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Waste</h1>
        <p className="mt-1 text-[12.5px] text-muted">
          Every misprint and spoil, and what it clusters on. A rate that follows
          one machine is a machine that needs looking at.
        </p>
      </div>

      <div className="mb-5">
        <RecordWaste />
      </div>

      {!showsCost && rows.length > 0 && (
        <p className="mb-5 rounded-[12px] border border-line bg-panel p-4 text-[14px] text-muted">
          You can see what was wasted and how much of it. What it cost is kept to
          the accounts, so this view shows quantities only.
        </p>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {(Object.keys(GROUP_LABEL) as GroupBy[]).map((g) => (
          <button
            key={g}
            type="button"
            onClick={() => setBy(g)}
            className={`rounded-[10px] border px-3 py-2 text-[14px] ${
              by === g ? 'border-line-strong font-semibold' : 'border-line text-muted'
            }`}
          >
            {GROUP_LABEL[g]}
          </button>
        ))}
        {/* The whole range, counted by the database. It used to be the number
            of rows on the page, which is the number of GROUPS, not the number
            of spoils, and was wrong twice over. */}
        <span className="ml-auto text-[14px] text-muted">
          {totals.entries.toLocaleString('en-AU')}{' '}
          {totals.entries === 1 ? 'entry' : 'entries'}
          {' · '}
          {totals.qty.toLocaleString('en-AU')} units
          {showsCost && totalCost > 0 && (
            <> · ${(totalCost / 100).toFixed(2)} in total</>
          )}
        </span>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-[10px] border border-line bg-panel p-8 text-center">
          <p className="text-[16px] font-semibold">No waste recorded.</p>
          <p className="mx-auto mt-2 max-w-[48ch] text-[14px] text-muted">
            Waste appears here when a misprint is recorded against a job. Every
            entry needs a reason, because waste nobody explains teaches nothing.
          </p>
        </div>
      ) : (
        <div className="rounded-[10px] border border-line bg-panel">
          <div
            className="grid gap-3 border-b border-line px-4 py-2 text-[12px] font-semibold
                       uppercase tracking-wide text-muted"
            style={{ gridTemplateColumns: `1fr 110px 110px${showsCost ? ' 120px' : ''}` }}
          >
            <span>{GROUP_LABEL[by]}</span>
            <span className="text-right">Entries</span>
            <span className="text-right">Quantity</span>
            {showsCost && <span className="text-right">Cost</span>}
          </div>

          {groups.map((g) => (
            <div
              key={g.bucket}
              className="grid items-center gap-3 border-b border-line px-[14px] text-[12.5px] last:border-0"
              style={{ gridTemplateColumns: `1fr 110px 110px${showsCost ? ' 120px' : ''}` }}
            >
              <span className="truncate">{g.bucket}</span>
              <span className="text-right text-muted">{num(g.entries)}</span>
              <span className="text-right font-semibold">{num(g.qty)}</span>
              {showsCost && (
                <span className="text-right">
                  ${(Number(g.cost_cents ?? 0) / 100).toFixed(2)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
